import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Plus, BookOpen, Users, Clock, Award, TrendingUp, CheckCircle2, Edit, Send, Trash2, Video, Settings, Upload, Download, Save, Loader2, RefreshCw, FileBadge, Building2, User } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import SendModal from '../components/SendModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Trainings = () => {
  const [trainings, setTrainings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    tipo: 'nr1',
    carga_horaria: 60,
    conteudo: [],
    pontos: 10,
    validade_dias: 365
  });
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editQuizQuestions, setEditQuizQuestions] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);

  // Certificate States
  const [certificates, setCertificates] = useState([]);
  const [certificateConfig, setCertificateConfig] = useState({
    intro_text: 'Certificamos que',
    default_hours: 1,
    signer_name: 'Responsável Técnico',
    signer_role: 'Coordenador de SST',
    signature_image: null,
    company_logo: null,
    auto_generate: true,
    notify_whatsapp: true
  });
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [savingCertConfig, setSavingCertConfig] = useState(false);
  const [activeTab, setActiveTab] = useState('catalog');

  // Função para extrair URL de vídeo do texto (YouTube, Vimeo, etc.)
  const extractVideoUrl = (text) => {
    if (!text) return null;
    
    // Regex patterns for video URLs
    const patterns = [
      // YouTube - various formats
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)?/i,
      // Vimeo
      /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)(\d+)(?:[^\s]*)?/i,
      // Dailymotion
      /(?:https?:\/\/)?(?:www\.)?(?:dailymotion\.com\/video\/)([a-zA-Z0-9]+)(?:[^\s]*)?/i,
    ];
    
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const fullUrl = match[0];
        
        // YouTube
        if (fullUrl.includes('youtube') || fullUrl.includes('youtu.be')) {
          const videoId = match[1];
          return {
            type: 'youtube',
            videoId,
            embedUrl: `https://www.youtube.com/embed/${videoId}`,
            originalUrl: fullUrl
          };
        }
        
        // Vimeo
        if (fullUrl.includes('vimeo')) {
          const videoId = match[1];
          return {
            type: 'vimeo',
            videoId,
            embedUrl: `https://player.vimeo.com/video/${videoId}`,
            originalUrl: fullUrl
          };
        }
        
        // Dailymotion
        if (fullUrl.includes('dailymotion')) {
          const videoId = match[1];
          return {
            type: 'dailymotion',
            videoId,
            embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
            originalUrl: fullUrl
          };
        }
      }
    }
    
    return null;
  };

  // Componente para exibir vídeo responsivo
  const VideoPlayer = ({ videoInfo }) => {
    if (!videoInfo) return null;
    
    return (
      <div className="mb-4">
        <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-slate-900">
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={videoInfo.embedUrl}
            title="Video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          <Video size={12} />
          Vídeo: {videoInfo.type.charAt(0).toUpperCase() + videoInfo.type.slice(1)}
        </p>
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      const [trainingsRes, enrollmentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/trainings`),
        axios.get(`${API_URL}/trainings/my-trainings`),
        axios.get(`${API_URL}/trainings/stats`)
      ]);
      setTrainings(trainingsRes.data);
      setEnrollments(enrollmentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar treinamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchData();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, [fetchData]);

  // Certificate Functions
  const fetchCertificateData = async () => {
    setLoadingCertificates(true);
    try {
      const [configRes, certsRes] = await Promise.all([
        axios.get(`${API_URL}/certificates/config`),
        axios.get(`${API_URL}/certificates`)
      ]);
      setCertificateConfig(configRes.data || {});
      setCertificates(certsRes.data || []);
    } catch (error) {
      console.error('Error fetching certificate data:', error);
    } finally {
      setLoadingCertificates(false);
    }
  };

  const saveCertificateConfig = async () => {
    setSavingCertConfig(true);
    try {
      await axios.put(`${API_URL}/certificates/config`, certificateConfig);
      toast.success('Configuração de certificados salva!');
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingCertConfig(false);
    }
  };

  const handleCertImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setCertificateConfig(prev => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const downloadCertificate = async (certificateId) => {
    try {
      const response = await axios.get(`${API_URL}/certificates/${certificateId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Certificado_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificado baixado!');
    } catch (error) {
      toast.error('Erro ao baixar certificado');
    }
  };

  const deleteCertificate = async (certificateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este certificado?')) return;
    
    try {
      await axios.delete(`${API_URL}/certificates/${certificateId}`);
      toast.success('Certificado excluído!');
      fetchCertificateData();
    } catch (error) {
      toast.error('Erro ao excluir certificado');
    }
  };

  // Load certificate data when tab changes
  useEffect(() => {
    if (activeTab === 'certificates') {
      fetchCertificateData();
    }
  }, [activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Mapear tipo para formato aceito pelo backend
      const tipoMap = {
        'nr1_geral': 'nr1',
        'nr1': 'nr1',
        'nr5': 'nr5',
        'nr6': 'nr6',
        'nr7': 'nr7',
        'nr9': 'nr9',
        'nr17': 'nr17',
        'psicossocial': 'psicossocial',
        'outros': 'outros'
      };
      
      const payload = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        tipo: tipoMap[formData.tipo] || 'nr1',
        carga_horaria: formData.carga_horaria || 60,
        conteudo: quizQuestions.length > 0 ? [{
          id: 'quiz1',
          titulo: 'Quiz de Avaliação',
          tipo: 'quiz',
          perguntas: quizQuestions
        }] : [],
        pontos: formData.pontos || 10,
        validade_dias: formData.validade_dias || 365
      };
      
      await axios.post(`${API_URL}/trainings`, payload);
      toast.success('Treinamento criado com sucesso!');
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Erro ao criar treinamento:', error.response?.data);
      toast.error('Erro ao criar treinamento: ' + (error.response?.data?.detail?.[0]?.msg || 'Erro desconhecido'));
    }
  };

  const handleEditTraining = (training) => {
    setEditData({
      id: training.id,
      titulo: training.titulo,
      descricao: training.descricao || '',
      tipo: training.tipo,
      carga_horaria: training.carga_horaria || training.duracao_minutos || 60,
      pontos: training.pontos || 10,
      validade_dias: training.validade_dias || 365
    });
    
    // Carregar perguntas do quiz existentes
    if (training.conteudo && training.conteudo.length > 0) {
      const quizContent = training.conteudo.find(c => c.tipo === 'quiz');
      if (quizContent && quizContent.perguntas) {
        setEditQuizQuestions(quizContent.perguntas);
      } else {
        setEditQuizQuestions([]);
      }
    } else {
      setEditQuizQuestions([]);
    }
    
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Preparar conteudo com quiz atualizado
      const conteudo = editQuizQuestions.length > 0 ? [{
        id: 'quiz1',
        titulo: 'Quiz de Avaliação',
        tipo: 'quiz',
        perguntas: editQuizQuestions
      }] : [];

      await axios.patch(`${API_URL}/trainings/${editData.id}`, {
        titulo: editData.titulo,
        descricao: editData.descricao,
        tipo: editData.tipo,
        carga_horaria: editData.carga_horaria,
        pontos: editData.pontos,
        validade_dias: editData.validade_dias,
        conteudo: conteudo
      });
      toast.success('Treinamento atualizado com sucesso!');
      setEditOpen(false);
      setEditQuizQuestions([]);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar:', error.response?.data);
      toast.error('Erro ao atualizar treinamento');
    }
  };

  const addEditQuizQuestion = () => {
    setEditQuizQuestions([
      ...editQuizQuestions,
      {
        id: `q${Date.now()}`,
        pergunta: '',
        opcoes: ['', '', '', ''],
        resposta_correta: 0
      }
    ]);
  };

  const updateEditQuizQuestion = (index, field, value) => {
    const updated = [...editQuizQuestions];
    updated[index][field] = value;
    setEditQuizQuestions(updated);
  };

  const removeEditQuizQuestion = (index) => {
    setEditQuizQuestions(editQuizQuestions.filter((_, i) => i !== index));
  };

  const handleSendTraining = (training) => {
    setSelectedTraining(training);
    setSendOpen(true);
  };

  const handleSendToEmployees = async (method, employeeIds) => {
    try {
      const employeesRes = await axios.get(`${API_URL}/employees`);
      const allEmployees = employeesRes.data;
      
      let successCount = 0;
      let failCount = 0;
      
      for (const empId of employeeIds) {
        const emp = allEmployees.find(e => e.id === empId);
        if (!emp) continue;
        
        const message = `📚 *Novo Treinamento Disponível*\n\n` +
            `*${selectedTraining.titulo}*\n\n` +
            `${selectedTraining.descricao || ''}\n\n` +
            `⏱ Duração: ${selectedTraining.carga_horaria || selectedTraining.duracao_minutos || 60} minutos\n` +
            `🏆 Pontos: ${selectedTraining.pontos || 10}\n\n` +
            `Acesse o sistema para realizar o treinamento.`;
        
        try {
          const phone = emp.whatsapp || emp.telefone;
          if (method === 'whatsapp' && phone) {
            await axios.post(`${API_URL}/whatsapp/send`, {
              phone: phone,
              message: message
            });
            successCount++;
          } else if (method === 'email' && emp.email) {
            await axios.post(`${API_URL}/send-email`, {
              to: emp.email,
              subject: `Novo Treinamento: ${selectedTraining.titulo}`,
              body: message.replace(/\*/g, '').replace(/\n/g, '<br>')
            });
            successCount++;
          }
        } catch (err) {
          console.error(`Erro ao enviar para ${emp.nome}:`, err.response?.data || err.message);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Treinamento enviado para ${successCount} funcionário(s)!`);
      }
      if (failCount > 0) {
        toast.warning(`${failCount} envio(s) falharam`);
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast.error('Erro ao enviar treinamento');
      throw error;
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      tipo: 'nr1',
      carga_horaria: 60,
      conteudo: [],
      pontos: 10,
      validade_dias: 365
    });
    setQuizQuestions([]);
  };

  const addQuizQuestion = () => {
    setQuizQuestions([
      ...quizQuestions,
      {
        id: `q${Date.now()}`,
        pergunta: '',
        opcoes: ['', '', '', ''],
        resposta_correta: 0
      }
    ]);
  };

  const updateQuizQuestion = (index, field, value) => {
    const updated = [...quizQuestions];
    updated[index][field] = value;
    setQuizQuestions(updated);
  };

  const getStatusBadge = (status) => {
    const badges = {
      nao_iniciado: <Badge className="bg-gray-500">Não Iniciado</Badge>,
      em_andamento: <Badge className="bg-blue-500">Em Andamento</Badge>,
      concluido: <Badge className="bg-emerald-500">Concluído</Badge>,
      reprovado: <Badge className="bg-red-500">Reprovado</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getTrainingTypeLabel = (tipo) => {
    const labels = {
      nr1_geral: 'Geral',
      nr1: 'Geral',
      nr5: 'NR-5 CIPA',
      nr6: 'NR-6 EPI',
      nr7: 'NR-7',
      nr9: 'NR-9',
      nr17: 'NR-17',
      gro: 'GRO',
      pgr: 'PGR',
      primeiros_socorros: 'Primeiros Socorros',
      prevencao_incendio: 'Prevenção de Incêndio',
      ergonomia: 'Ergonomia',
      trabalho_altura: 'Trabalho em Altura',
      eletricidade: 'Eletricidade',
      psicossocial: 'Psicossocial',
      outros: 'Outros',
      outro: 'Outro'
    };
    return labels[tipo] || tipo;
  };

  // Filter out DDS items - Trainings module only shows formal courses
  const filteredTrainings = trainings.filter(t => t.tipo !== 'dds');

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="trainings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Treinamentos
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gerencie treinamentos e cursos da sua empresa
          </p>
        </div>
        <div className="flex gap-2">
          {/* Botão Novo Treinamento */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="add-training-button"
              >
                <Plus size={20} className="mr-2" />
                Novo Treinamento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Novo Treinamento</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="titulo">Título do Treinamento</Label>
                  <Input
                    id="titulo"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  required
                  data-testid="training-titulo-input"
                />
              </div>
              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={3}
                  required
                />
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Video size={12} className="text-red-500" />
                  Para incluir um vídeo, cole o link (YouTube, Vimeo, etc.) na descrição ou conteúdo.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tipo">Tipo de Treinamento</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nr1_geral">Geral</SelectItem>
                      <SelectItem value="gro">GRO</SelectItem>
                      <SelectItem value="pgr">PGR</SelectItem>
                      <SelectItem value="primeiros_socorros">Primeiros Socorros</SelectItem>
                      <SelectItem value="prevencao_incendio">Prevenção de Incêndio</SelectItem>
                      <SelectItem value="ergonomia">Ergonomia</SelectItem>
                      <SelectItem value="trabalho_altura">Trabalho em Altura</SelectItem>
                      <SelectItem value="eletricidade">Eletricidade</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="duracao">Duração (minutos)</Label>
                  <Input
                    id="duracao"
                    type="number"
                    value={formData.duracao_minutos}
                    onChange={(e) => setFormData({ ...formData, duracao_minutos: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="conteudo">Conteúdo do Treinamento</Label>
                <Textarea
                  id="conteudo"
                  value={formData.conteudo}
                  onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
                  rows={6}
                  required
                  placeholder="Descreva o conteúdo completo do treinamento..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nota_minima">Nota Mínima (%)</Label>
                  <Input
                    id="nota_minima"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.nota_minima}
                    onChange={(e) => setFormData({ ...formData, nota_minima: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="validade">Validade (meses)</Label>
                  <Input
                    id="validade"
                    type="number"
                    value={formData.validade_meses}
                    onChange={(e) => setFormData({ ...formData, validade_meses: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label>Perguntas do Quiz</Label>
                  <Button type="button" size="sm" onClick={addQuizQuestion}>
                    <Plus size={16} className="mr-2" />
                    Adicionar Pergunta
                  </Button>
                </div>
                {quizQuestions.map((q, idx) => (
                  <div key={q.id} className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg mb-3">
                    <Label>Pergunta {idx + 1}</Label>
                    <Input
                      value={q.pergunta}
                      onChange={(e) => updateQuizQuestion(idx, 'pergunta', e.target.value)}
                      placeholder="Digite a pergunta..."
                      className="mb-2"
                    />
                    {q.opcoes.map((opcao, oIdx) => (
                      <Input
                        key={oIdx}
                        value={opcao}
                        onChange={(e) => {
                          const newOpcoes = [...q.opcoes];
                          newOpcoes[oIdx] = e.target.value;
                          updateQuizQuestion(idx, 'opcoes', newOpcoes);
                        }}
                        placeholder={`Opção ${oIdx + 1}`}
                        className="mb-2"
                      />
                    ))}
                    <Select
                      value={q.resposta_correta.toString()}
                      onValueChange={(value) => updateQuizQuestion(idx, 'resposta_correta', parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Resposta correta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Opção 1</SelectItem>
                        <SelectItem value="1">Opção 2</SelectItem>
                        <SelectItem value="2">Opção 3</SelectItem>
                        <SelectItem value="3">Opção 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <Button type="submit" className="w-full">
                Criar Treinamento
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                <BookOpen size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 dark:bg-emerald-900 p-2 rounded-lg">
                <CheckCircle2 size={20} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Concluídos</p>
                <p className="text-2xl font-bold">{stats.concluidos}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                <Clock size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Em Andamento</p>
                <p className="text-2xl font-bold">{stats.em_andamento}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-gray-100 dark:bg-gray-900 p-2 rounded-lg">
                <Users size={20} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pendentes</p>
                <p className="text-2xl font-bold">{stats.pendentes}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 dark:bg-red-900 p-2 rounded-lg">
                <Award size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Vencidos</p>
                <p className="text-2xl font-bold">{stats.vencidos}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
                <TrendingUp size={20} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Taxa Conclusão</p>
                <p className="text-2xl font-bold">{stats.taxa_conclusao}%</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="catalog">Catálogo ({trainings.length})</TabsTrigger>
          <TabsTrigger value="enrollments">Matrículas ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <FileBadge size={16} />
            Certificados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTrainings.map((training) => {
              const videoInfo = extractVideoUrl(training.descricao) || 
                               (training.conteudo && training.conteudo[0]?.texto && extractVideoUrl(training.conteudo[0].texto));
              
              return (
                <Card key={training.id} className="p-6">
                  {/* Video Player se houver vídeo */}
                  {videoInfo && <VideoPlayer videoInfo={videoInfo} />}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                      <BookOpen size={24} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex gap-1">
                      {videoInfo && (
                        <Badge className="bg-red-500">
                          <Video size={12} className="mr-1" />
                          Vídeo
                        </Badge>
                      )}
                      <Badge className="bg-emerald-500">
                        {getTrainingTypeLabel(training.tipo)}
                      </Badge>
                    </div>
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2">{training.titulo}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                    {training.descricao?.replace(/https?:\/\/[^\s]+/g, '').trim() || training.descricao}
                  </p>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-500" />
                      <span>{training.carga_horaria || training.duracao_minutos || 60} minutos</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award size={16} className="text-slate-500" />
                      <span>Pontos: {training.pontos || 10}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-slate-500" />
                      <span>Validade: {training.validade_dias ? Math.round(training.validade_dias/30) : (training.validade_meses || 12)} meses</span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-3 border-t">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleEditTraining(training)}
                      className="flex-1"
                    >
                      <Edit size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleSendTraining(training)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Send size={14} className="mr-1" />
                      Enviar
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="enrollments" className="space-y-4 mt-6">
          <div className="space-y-3">
            {enrollments.map((enrollment) => (
              <Card key={enrollment.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {enrollment.training.titulo}
                      </h3>
                      {getStatusBadge(enrollment.status)}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      Funcionário: {enrollment.employee.nome} - {enrollment.employee.cargo}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span>Progresso: {enrollment.progresso}%</span>
                      {enrollment.nota_quiz && (
                        <span className={enrollment.nota_quiz >= enrollment.training.nota_minima ? 'text-emerald-500' : 'text-red-500'}>
                          Nota: {enrollment.nota_quiz}%
                        </span>
                      )}
                    </div>
                    {enrollment.data_validade && (
                      <p className="text-xs text-slate-500 mt-2">
                        Válido até: {new Date(enrollment.data_validade).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  {/* Generate Certificate Button for completed trainings */}
                  {enrollment.status === 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-purple-600 hover:text-purple-700"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.post(
                            `${API}/api/certificates/generate-training`,
                            {
                              training_id: enrollment.training_id,
                              employee_id: enrollment.employee_id
                            },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (response.data.already_exists) {
                            toast.info('Certificado já foi gerado anteriormente');
                          } else {
                            toast.success('Certificado gerado com sucesso!');
                          }
                          fetchCertificateData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erro ao gerar certificado');
                        }
                      }}
                      data-testid={`generate-cert-${enrollment.id}`}
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Gerar Certificado
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-6 mt-6">
          {/* Certificate Configuration Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-500" />
                <h3 className="text-lg font-semibold">Configuração dos Certificados</h3>
              </div>
              <Button 
                onClick={saveCertificateConfig}
                disabled={savingCertConfig}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingCertConfig ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar Configuração
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Text Fields */}
              <div className="space-y-4">
                <div>
                  <Label>Texto de Introdução</Label>
                  <Input
                    value={certificateConfig.intro_text || ''}
                    onChange={(e) => setCertificateConfig(prev => ({ ...prev, intro_text: e.target.value }))}
                    placeholder="Certificamos que"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">Aparece antes do nome do funcionário</p>
                </div>

                <div>
                  <Label>Carga Horária Padrão (horas)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={certificateConfig.default_hours || 1}
                    onChange={(e) => setCertificateConfig(prev => ({ ...prev, default_hours: parseFloat(e.target.value) }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Nome do Responsável pela Assinatura</Label>
                  <Input
                    value={certificateConfig.signer_name || ''}
                    onChange={(e) => setCertificateConfig(prev => ({ ...prev, signer_name: e.target.value }))}
                    placeholder="Nome completo"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Cargo do Responsável</Label>
                  <Input
                    value={certificateConfig.signer_role || ''}
                    onChange={(e) => setCertificateConfig(prev => ({ ...prev, signer_role: e.target.value }))}
                    placeholder="Ex: Coordenador de SST"
                    className="mt-1"
                  />
                </div>

                {/* Checkboxes */}
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="auto_generate"
                      checked={certificateConfig.auto_generate}
                      onCheckedChange={(checked) => setCertificateConfig(prev => ({ ...prev, auto_generate: checked }))}
                    />
                    <label htmlFor="auto_generate" className="text-sm text-slate-600 cursor-pointer">
                      Gerar automaticamente ao completar
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="notify_whatsapp"
                      checked={certificateConfig.notify_whatsapp}
                      onCheckedChange={(checked) => setCertificateConfig(prev => ({ ...prev, notify_whatsapp: checked }))}
                    />
                    <label htmlFor="notify_whatsapp" className="text-sm text-slate-600 cursor-pointer">
                      Notificar via WhatsApp
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column - Image Uploads */}
              <div className="space-y-4">
                {/* Company Logo */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Logo da Empresa
                  </Label>
                  <div className="mt-2 flex items-center gap-4">
                    {certificateConfig.company_logo ? (
                      <div className="relative">
                        <img 
                          src={certificateConfig.company_logo} 
                          alt="Logo" 
                          className="h-16 w-auto rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => setCertificateConfig(prev => ({ ...prev, company_logo: null }))}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg hover:border-purple-500 transition-colors">
                        <Upload className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-500">Upload Logo</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => handleCertImageUpload(e, 'company_logo')}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">PNG ou JPG, máx 2MB</p>
                </div>

                {/* Signature Image */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Imagem da Assinatura
                  </Label>
                  <div className="mt-2 flex items-center gap-4">
                    {certificateConfig.signature_image ? (
                      <div className="relative">
                        <img 
                          src={certificateConfig.signature_image} 
                          alt="Assinatura" 
                          className="h-12 w-auto rounded border bg-white p-1"
                        />
                        <button
                          type="button"
                          onClick={() => setCertificateConfig(prev => ({ ...prev, signature_image: null }))}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg hover:border-purple-500 transition-colors">
                        <Upload className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-500">Upload Assinatura</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => handleCertImageUpload(e, 'signature_image')}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Imagem com fundo transparente (PNG) recomendada</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Certificates List Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                <h3 className="text-lg font-semibold">Certificados Emitidos</h3>
                <Badge variant="secondary">{certificates.length}</Badge>
              </div>
              <Button variant="outline" onClick={fetchCertificateData} disabled={loadingCertificates}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingCertificates ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>

            {loadingCertificates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : certificates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Award className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum certificado emitido ainda</p>
                <p className="text-sm mt-1">Certificados serão gerados quando funcionários completarem treinamentos</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-slate-500">
                      <th className="pb-3 font-medium">Funcionário</th>
                      <th className="pb-3 font-medium">Categoria</th>
                      <th className="pb-3 font-medium">Carga Horária</th>
                      <th className="pb-3 font-medium">Data</th>
                      <th className="pb-3 font-medium text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map(cert => (
                      <tr key={cert.id} className="border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{cert.employee_name}</p>
                              <p className="text-xs text-slate-400">{cert.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant="outline">{cert.category_name}</Badge>
                        </td>
                        <td className="py-3 text-sm">
                          {cert.total_hours?.toFixed(1)}h
                        </td>
                        <td className="py-3 text-sm text-slate-500">
                          {new Date(cert.completion_date).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => downloadCertificate(cert.id)}
                              className="text-purple-600 hover:text-purple-700"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Baixar
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteCertificate(cert.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {trainings.length === 0 && (
        <Card className="p-12 text-center">
          <BookOpen size={48} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Nenhum treinamento cadastrado. Clique em "Novo Treinamento" para começar.
          </p>
        </Card>
      )}

      {/* Modal de Edição */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Treinamento</DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={editData.titulo}
                  onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editData.tipo} onValueChange={(v) => setEditData({ ...editData, tipo: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nr1">Geral</SelectItem>
                      <SelectItem value="nr5">NR-5 CIPA</SelectItem>
                      <SelectItem value="nr6">NR-6 EPI</SelectItem>
                      <SelectItem value="psicossocial">Psicossocial</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Carga Horária (min)</Label>
                  <Input
                    type="number"
                    value={editData.carga_horaria}
                    onChange={(e) => setEditData({ ...editData, carga_horaria: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pontos</Label>
                  <Input
                    type="number"
                    value={editData.pontos}
                    onChange={(e) => setEditData({ ...editData, pontos: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Validade (dias)</Label>
                  <Input
                    type="number"
                    value={editData.validade_dias}
                    onChange={(e) => setEditData({ ...editData, validade_dias: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Seção de Quiz */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-semibold">Perguntas do Quiz</Label>
                  <Button type="button" size="sm" onClick={addEditQuizQuestion} variant="outline">
                    <Plus size={16} className="mr-2" />
                    Adicionar Pergunta
                  </Button>
                </div>
                
                {editQuizQuestions.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    Nenhuma pergunta cadastrada. Clique em "Adicionar Pergunta" para criar o quiz.
                  </p>
                )}

                {editQuizQuestions.map((q, idx) => (
                  <div key={q.id} className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg mb-3 relative">
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="ghost"
                      onClick={() => removeEditQuizQuestion(idx)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                    <Label className="text-emerald-600">Pergunta {idx + 1}</Label>
                    <Input
                      value={q.pergunta}
                      onChange={(e) => updateEditQuizQuestion(idx, 'pergunta', e.target.value)}
                      placeholder="Digite a pergunta..."
                      className="mb-2 mt-1"
                    />
                    <Label className="text-sm text-slate-500">Opções de resposta:</Label>
                    {q.opcoes.map((opcao, oIdx) => (
                      <Input
                        key={oIdx}
                        value={opcao}
                        onChange={(e) => {
                          const newOpcoes = [...q.opcoes];
                          newOpcoes[oIdx] = e.target.value;
                          updateEditQuizQuestion(idx, 'opcoes', newOpcoes);
                        }}
                        placeholder={`Opção ${oIdx + 1}`}
                        className="mb-2"
                      />
                    ))}
                    <Label className="text-sm text-slate-500">Resposta correta:</Label>
                    <Select
                      value={q.resposta_correta.toString()}
                      onValueChange={(value) => updateEditQuizQuestion(idx, 'resposta_correta', parseInt(value))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecione a resposta correta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Opção 1</SelectItem>
                        <SelectItem value="1">Opção 2</SelectItem>
                        <SelectItem value="2">Opção 3</SelectItem>
                        <SelectItem value="3">Opção 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
                <Button onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700">
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Envio */}
      <SendModal
        open={sendOpen}
        onOpenChange={setSendOpen}
        title="Enviar Treinamento"
        itemTitle={selectedTraining?.titulo}
        itemDescription={selectedTraining?.descricao}
        onSend={handleSendToEmployees}
        showMethodSelector={true}
        defaultMethod="email"
      />
    </div>
  );
};

export default Trainings;
